import React, { useEffect } from 'react';
import {
    Box,
    Container,
    Grid,
    Card,
    CardContent,
    CardActions,
    Button,
    Typography,
    CircularProgress,
    Alert,
    Chip,
    Hidden,
    Avatar,
} from '@mui/material';
import { DeleteOutlined, Visibility } from '@mui/icons-material';
import ConfirmationDialog from '../../../components/confirm-dialog';
import Api from '../../../store/api';
import { useLocalState } from '../../../hooks';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import './sent-interests.css';

interface InterestItem {
    id: number;
    fromId: number;
    toId: number;
    state: string;
    message: string;
    createdAt: string;
    viewedAt: string | null;
    acceptedAt: string | null;
    rejectedAt: string | null;
    updatedAt: string;
}

interface ProfileInfo {
    id: number;
    memberId?: string;
    firstName?: string;
    lastName?: string;
    age?: number;
    state?: string;
    city?: string;
    img?: string;
}

const SentInterests: React.FC = () => {
    const navigate = useNavigate();
    const [LS, setLS] = useLocalState({
        interests: [] as InterestItem[],
        loading: false,
        error: '',
        confirmOpen: false,
        selectedInterestId: null as number | null,
        isCanceling: false,
        profilesById: {} as Record<number, ProfileInfo>,
    });

    const authUserId = useSelector((state: any) => state.auth?.user?.id);

    useEffect(() => {
        if (authUserId) {
            fetchSentInterests();
        }
    }, [authUserId]);

    const fetchSentInterests = async () => {
        if (!authUserId) return;
        setLS({ loading: true, error: '' });
        try {
            const response = await Api.getInterestSent(authUserId);
            if (response?.data?.data) {
                const interests: InterestItem[] = response.data.data;
                const uniqueUserIds = Array.from(new Set(interests.map((item) => item.toId)));
                const profileResults = await Promise.all(
                    uniqueUserIds.map(async (userId) => {
                        try {
                            const profile = await Api.getProfileDetails(userId);
                            return [userId, profile] as const;
                        } catch (e) {
                            return [userId, null] as const;
                        }
                    })
                );
                const profilesById = profileResults.reduce((acc, [userId, profile]) => {
                    if (profile) acc[userId] = profile;
                    return acc;
                }, {} as Record<number, ProfileInfo>);

                setLS({ interests, profilesById, loading: false });
            } else {
                setLS({ error: 'Failed to fetch sent interests', loading: false });
            }
        } catch (error: any) {
            setLS({ error: error.message || 'Error fetching interests', loading: false });
        }
    };

    const handleCancel = (interestId: number) => {
        setLS({ selectedInterestId: interestId, confirmOpen: true });
    };

    const confirmCancel = async () => {
        if (LS.selectedInterestId && authUserId) {
            setLS({ isCanceling: true });
            try {
                const interest = LS.interests.find((i: InterestItem) => i.id === LS.selectedInterestId);
                if (interest) {
                    await Api.cancelInterest(authUserId, interest.toId);
                    setLS({
                        interests: LS.interests.filter((i: InterestItem) => i.id !== LS.selectedInterestId),
                        isCanceling: false,
                    });
                }
            } catch (error: any) {
                setLS({ isCanceling: false, error: error.message || 'Failed to cancel interest' });
            }
        }
        setLS({ confirmOpen: false, selectedInterestId: null });
    };

    const getStatusColor = (state: string) => {
        switch (state) {
            case 'SENT':
                return 'warning';
            case 'VIEWED':
                return 'info';
            case 'ACCEPTED':
                return 'success';
            case 'REJECTED':
                return 'error';
            default:
                return 'default';
        }
    };

    const formatDate = (dateString: string | null) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const canCancel = (interest: InterestItem) => {
        return interest.state === 'SENT' || interest.state === 'VIEWED';
    };

    const openProfile = (userId: number) => {
        const profile = LS.profilesById[userId];
        if (!profile) return;
        sessionStorage.setItem('selectedProfile', JSON.stringify(profile));
        navigate('/user/view-profile');
    };

    return (
        <>
            <Hidden mdUp>
                <Box sx={{ height: '50px' }} />
            </Hidden>
            <Box sx={{ p: 3 }}>
                <Container maxWidth="lg">
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                            Sent Interests
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Track your sent interests and their status
                        </Typography>
                    </Box>

                    {LS.error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {LS.error}
                        </Alert>
                    )}

                    {LS.loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                            <CircularProgress />
                        </Box>
                    ) : LS.interests.length === 0 ? (
                        <Card sx={{ textAlign: 'center', py: 8 }}>
                            <Typography variant="h6" color="textSecondary">
                                No sent interests yet
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                Start sending interests to members you like
                            </Typography>
                        </Card>
                    ) : (
                        <Grid container spacing={3}>
                            {LS.interests.map((interest: InterestItem) => {
                                const profile = LS.profilesById[interest.toId];
                                const name = `${profile?.firstName || ''} ${profile?.lastName || ''}`.trim() || `Profile #${interest.toId}`;
                                return (
                                <Grid item xs={12} md={6} key={interest.id}>
                                    <Card
                                        sx={{ height: '100%', display: 'flex', flexDirection: 'column', cursor: 'pointer' }}
                                        onClick={() => openProfile(interest.toId)}
                                    >
                                        <CardContent sx={{ pb: 1, flexGrow: 1 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                                                <Box
                                                    sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }}
                                                    onClick={() => openProfile(interest.toId)}
                                                >
                                                    <Avatar src={profile?.img} alt={name} sx={{ width: 44, height: 44 }} />
                                                    <Box>
                                                        <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                                                            {name}
                                                        </Typography>
                                                        <Typography variant="caption" color="textSecondary" display="block">
                                                            Profile ID: {profile?.memberId || interest.toId}
                                                        </Typography>
                                                        <Typography variant="caption" color="textSecondary" display="block">
                                                            Age: {profile?.age ? `${profile.age} Years` : '-'}
                                                        </Typography>
                                                        <Typography variant="caption" color="textSecondary" display="block">
                                                            {profile?.city || '-'}, {profile?.state || '-'}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <Chip
                                                    label={interest.state}
                                                    color={getStatusColor(interest.state) as any}
                                                    size="small"
                                                />
                                            </Box>

                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="caption" color="textSecondary" display="block">
                                                    Sent: {formatDate(interest.createdAt)}
                                                </Typography>
                                                {interest.viewedAt && (
                                                    <Typography variant="caption" color="info.main" display="block" sx={{ mt: 0.5 }}>
                                                        <Visibility sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                                                        Viewed: {formatDate(interest.viewedAt)}
                                                    </Typography>
                                                )}
                                                {interest.acceptedAt && (
                                                    <Typography variant="caption" sx={{ color: '#2e7d32', display: 'block', mt: 0.5 }}>
                                                        ✓ Accepted: {formatDate(interest.acceptedAt)}
                                                    </Typography>
                                                )}
                                                {interest.rejectedAt && (
                                                    <Typography variant="caption" sx={{ color: '#d32f2f', display: 'block', mt: 0.5 }}>
                                                        ✗ Rejected: {formatDate(interest.rejectedAt)}
                                                    </Typography>
                                                )}
                                            </Box>

                                            {interest.message && (
                                                <Box sx={{ mt: 2 }}>
                                                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                        <strong>Message:</strong>
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                        sx={{
                                                            p: 1,
                                                            bgcolor: '#f5f5f5',
                                                            borderRadius: 1,
                                                            maxHeight: '80px',
                                                            overflow: 'auto',
                                                        }}
                                                    >
                                                        {interest.message}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </CardContent>

                                        <CardActions>
                                            {canCancel(interest) && (
                                                <Button
                                                    size="small"
                                                    startIcon={<DeleteOutlined />}
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        handleCancel(interest.id);
                                                    }}
                                                    sx={{ color: '#d32f2f' }}
                                                >
                                                    Cancel
                                                </Button>
                                            )}
                                        </CardActions>
                                    </Card>
                                </Grid>
                            )})}
                        </Grid>
                    )}
                </Container>
            </Box>

            <ConfirmationDialog
                open={LS.confirmOpen}
                onClose={() => setLS({ confirmOpen: false })}
                onConfirm={confirmCancel}
                title="Cancel Interest"
                message="Are you sure you want to cancel this interest? This action cannot be undone."
                confirmText="Cancel Interest"
                confirmColor="error"
                isLoading={LS.isCanceling}
            />
        </>
    );
};

export default SentInterests;
